import { useState } from "react";
import { Button } from "flowbite-react";
import { HiPencil, HiTrash } from "react-icons/hi";
import { EventStatus } from "@/sharedTypes";
import DataTable from "@/components/Table";
import { useUpdateEventStatusMutation, useDeleteEventStatusMutation } from "@/api/locationApi/eventStatus";
import { toast } from "@/lib/toast";
import { getErrorMessage } from "@/utils/requestUtils";
import EventStatusFormDialog from "./EventStatusFormDialog";
import ConfirmationDialog from "@/components/ConfirmationDialog";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { isCompanyAdminUser } from "@/constants/agent";
import { useTranslation } from "react-i18next";

interface EventStatusListTableProps {
  data: EventStatus[];
  pagination: any;
  onPageChange: (page: number) => void;
}

export default function EventStatusListTable({ data, pagination, onPageChange }: EventStatusListTableProps) {
  const { t } = useTranslation(["temporalActivity", "common"]);
  const user = useCurrentUser();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedEventStatus, setSelectedEventStatus] = useState<EventStatus | undefined>();

  const [updateEventStatus] = useUpdateEventStatusMutation();
  const [deleteEventStatus] = useDeleteEventStatusMutation();

  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
    onConfirm: () => void;
  } | null>(null);

  const handleEdit = (eventStatus: EventStatus) => {
    setSelectedEventStatus(eventStatus);
    setIsEditModalOpen(true);
  };

  const handleEditSubmit = async (data: EventStatus) => {
    if (!selectedEventStatus) return;
    try {
      await updateEventStatus({ ...data, id: selectedEventStatus.id } as EventStatus).unwrap();
      toast.success(t("temporalActivity:eventStatus.updatedSuccess"));
      setIsEditModalOpen(false);
      setSelectedEventStatus(undefined);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const handleDelete = (eventStatus: EventStatus) => {
    setConfirmDialog({
      open: true,
      title: t("temporalActivity:eventStatus.confirmDeleteTitle"),
      description: t("temporalActivity:eventStatus.confirmDeleteBody"),
      onConfirm: async () => {
        try {
          await deleteEventStatus(eventStatus.id).unwrap();
          toast.success(t("temporalActivity:eventStatus.deletedSuccess"));
        } catch (err) {
          toast.error(getErrorMessage(err));
        }
        setConfirmDialog(null);
      },
    });
  };

  const columns = [
    { key: "id", label: t("temporalActivity:eventStatus.ref") },
    { key: "status", label: t("temporalActivity:eventStatus.name") },
    { key: "description", label: t("temporalActivity:eventStatus.description") },
    ...(isCompanyAdminUser(user)
      ? [
          {
            key: "actions",
            label: t("temporalActivity:actions"),
            render: (row: EventStatus) => (
              <div className="flex gap-2">
                <Button size="xs" color="light" onClick={() => handleEdit(row)}>
                  <HiPencil className="h-4 w-4 text-purple-600" />
                </Button>
                <Button size="xs" color="light" onClick={() => handleDelete(row)}>
                  <HiTrash className="h-4 w-4 text-red-600" />
                </Button>
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <DataTable columns={columns} data={data} pagination={pagination} onPageChange={onPageChange} />

      <EventStatusFormDialog
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSubmit={handleEditSubmit}
        defaultValues={selectedEventStatus}
        mode="edit"
      />

      {confirmDialog && (
        <ConfirmationDialog
          title={confirmDialog.title}
          body={confirmDialog.description}
          onCancel={() => setConfirmDialog(null)}
          onConfirm={confirmDialog.onConfirm}
        />
      )}
    </>
  );
}
